import { useAsyncExecutor, useAsyncExecutorPool } from "async-executor-hook";
import { Toast } from "primereact/toast";
import { useEffect, useReducer, useRef, useState } from "react";
import {
  FormControl,
  FormGroup,
  useFormConfig,
  Validators,
  WForm,
  WFormControl,
} from "rectangular-forms";
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  from,
  map,
  mergeMap,
  of,
  tap,
} from "rxjs";

import { Authorization } from "../../common/AuthorizationCpt";
import {
  inputMaxLengthFn,
  inputUperCaseFn,
  onlyDigits,
  WDropdown,
  WInputText,
  WMultiSelect,
} from "../../common/components/TemplateContainer/InputTemplates";
import { SaveButton } from "../../common/components/TemplateContainer/SaveButton";
import { TemplateInputContainer } from "../../common/components/TemplateContainer/TemplateContainer";
import { getErrorValueResponse } from "../../common/error/Error";
import IconVectorRightBlue from "../../images/icon_button_users_blue.svg";
import { HTTP_METHODS } from "../../utils/constants";
import { networkBackendRequest } from "../../utils/networkUtils";
import {
  DOCUMENT_DNI_MAX_LENGTH,
  DOCUMENT_NUMBER_MAX_LENGTH,
  DOCUMENT_TYPE_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  FULL_NAME_MAX_LENGTH,
  JOB_MAX_LENGTH,
  PASSPORT_NUMBER_MAX_LENGTH,
  PASSPORT_NUMBER_MIN_LENGTH,
  PASSWORD_CREATE_USER,
  PHONE_NUMBER_MAX_LENGTH,
  TYPE_PASSPORT_NUMBER_MIN_LENGTH,
} from "../../utils/valueStaticConstants";
import { DivOverMaxHeigthIntra } from "./UsersManagementStyles";
const UsersManagementStepTwoForm = (props) => {
  const {
    toggleModal,
    dataInitialUsers,
    dataUserEdit,
    showModalHide,
    userProfileMe,
    setMessageTypeEditCreate,
    settitleTypeEditCreate,
    userInfo,
    showProfileTwoShowUser,
  } = props;

  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const operationNumberValidation =
    (forceUpdate: () => void) => (formControl: FormControl) => {
      const subject: any = new BehaviorSubject("");
      subject.next(formControl.value);
      const debouncerTime$ = subject.asObservable().pipe(
        debounceTime(1000),
        mergeMap((email: string) => {
          return from(
            networkBackendRequest(
              "/users/check-email/",
              HTTP_METHODS.GET,
              { email },
              undefined
            ).then((resp) => resp.json())
          ).pipe(
            map((isAvailable: boolean) => {
              return !isAvailable
                ? {
                    emailIsTaken: true,
                  }
                : null;
            }),
            catchError(() => {
              return of({ emailIsTaken: true });
            }),
            tap((x) => {
              setTimeout(() => {
                forceUpdate();
              }, 40);
            })
          );
        })
      );
      return debouncerTime$;
    };

  const toast: any = useRef();
  const formConfig = useFormConfig({
    createForm: () => {
      const form = new FormGroup({
        user: new FormGroup({
          full_name: new FormControl(null, [
            Validators.required,
            Validators.maxLength(FULL_NAME_MAX_LENGTH),
            Validators.pattern(/^[A-Z \-ÁÉÍÓÚÑ]*$/),
          ]),
          email: new FormControl(
            null,
            [
              Validators.required,
              Validators.pattern(/^(.+)@(.+)\.(.+)$/),
              Validators.maxLength(EMAIL_MAX_LENGTH),
            ],
            operationNumberValidation(forceUpdate)
          ),
          document_type: new FormControl(null, [Validators.required]),
          document_number: new FormControl(null, [
            Validators.required,
            Validators.maxLength(DOCUMENT_NUMBER_MAX_LENGTH),
          ]),
          phone_number: new FormControl(null, [
            Validators.required,
            Validators.pattern(/^[0-9]{7,15}$/),
            Validators.maxLength(PHONE_NUMBER_MAX_LENGTH),
          ]),
          job: new FormControl(null, [
            Validators.required,
            Validators.maxLength(JOB_MAX_LENGTH),
          ]),
          authorization_doc: new FormControl(null, []),
          rol: new FormControl(null, []),
        }),
        profile_id: new FormControl(null, [Validators.required]),
        processes: new FormControl([], [Validators.required]),
      });
      if (
        userInfo.email === dataUserEdit.email &&
        userInfo.profile.name === "Administrador"
      ) {
        form.disable();
      } else {
        form.get("user.rol").disable();
      }
      form
        .get("user.document_type")
        .valueChanges.pipe(
          tap((document_type) => {
            const document_numberFC = form.get("user.document_number");
            document_numberFC.clearValidators();
            if (document_type === "dni") {
              document_numberFC.setValidators([
                Validators.required,
                Validators.minLength(DOCUMENT_DNI_MAX_LENGTH),
                Validators.maxLength(DOCUMENT_DNI_MAX_LENGTH),
              ]);
              setSocumentNumberMaxLength(DOCUMENT_DNI_MAX_LENGTH);
            } else if (document_type === "passport") {
              document_numberFC.setValidators([
                Validators.required,
                Validators.minLength(TYPE_PASSPORT_NUMBER_MIN_LENGTH),
                Validators.maxLength(PASSPORT_NUMBER_MAX_LENGTH),
              ]);
              setSocumentNumberMaxLength(PASSPORT_NUMBER_MAX_LENGTH);
            } else {
              document_numberFC.setValidators([
                Validators.required,
                Validators.minLength(PASSPORT_NUMBER_MIN_LENGTH),
                Validators.maxLength(DOCUMENT_TYPE_MAX_LENGTH),
              ]);
              setSocumentNumberMaxLength(DOCUMENT_TYPE_MAX_LENGTH);
            }
            document_numberFC.updateValueAndValidity();
          })
        )
        .subscribe();
      if (dataUserEdit) {
        form.get("user.email").disable();
      }
      return form;
    },
    onFormLoaded: (form, data) => {
      form.patchValue({ ...data, processes: data.processes || [] });
    },
    onSubmit: (form, { processing, succeed, failed }) => {
      if (form.invalid) {
        toast.current.show({
          severity: "error",
          summary:
            "Completar todos los campos obligatorios antes de continuar. ¡Gracias!",
          contentClassName: "",
        });
        return;
      }
      processing();
      createUser.execute({ data: form.value, succeed, failed, dataUserEdit });
    },
  });

  const { loadSucceed } = formConfig;

  const valueRolMe = [].concat(userProfileMe.role);
  const valueRolDataMe = valueRolMe.map((rolMe) => {
    return { label: rolMe.name, value: rolMe.id };
  });
  const idRol = valueRolDataMe && valueRolDataMe.find((idRol) => idRol.value);

  useEffect(() => {
    const data = {
      ...(dataUserEdit || dataInitialUsers),
      user: {
        full_name: dataUserEdit && dataUserEdit.full_name,
        email: dataUserEdit && dataUserEdit.email,
        document_type: dataUserEdit ? dataUserEdit.document_type : "dni",
        document_number: dataUserEdit && dataUserEdit.document_number,
        phone_number: dataUserEdit && dataUserEdit.phone_number,
        job: dataUserEdit ? dataUserEdit.job : "",
        rol: dataUserEdit ? dataUserEdit.role.id : idRol.value,
      },
      profile_id: dataUserEdit && dataUserEdit.role.profile.id,
      processes:
        dataUserEdit &&
        dataUserEdit.processes.map((process) => {
          return process.id;
        }),
    };
    loadSucceed(data);
  }, [dataUserEdit, loadSucceed, dataInitialUsers, idRol.value]);

  const createUser = useAsyncExecutor(
    (params: any) => {
      const { data, dataUserEdit } = params;
      const idEditUser = dataUserEdit.id;
      const body = {
        ...data,
        processes: data.processes && data.processes,
      };
      const bodyEdit = {
        user: {
          document_number: data.user.document_number,
          document_type: data.user.document_type,
          full_name: data.user.full_name,
          job: data.user.job,
          phone_number: data.user.phone_number,
        },
        processes: data.processes && data.processes,
        profile_id: data.profile_id,
      };
      return networkBackendRequest(
        idEditUser ? `/users/${idEditUser}/` : `/users/`,
        idEditUser ? HTTP_METHODS.PATCH : HTTP_METHODS.POST,
        {},
        idEditUser ? bodyEdit : body
      ).then((resp) => resp.json());
    },
    {
      onSuccess: (resp, params: any) => {
        params.succeed();
        const { succeed, data, dataUserEdit } = params;
        if (dataUserEdit) {
          setMessageTypeEditCreate(
            `El usuario ${resp.full_name} ha sido actualizado exitosamente`
          );
          settitleTypeEditCreate("Usuario actualizado exitosamente");
        } else {
          setMessageTypeEditCreate(
            `Se ha registrado el usuario ${data.user.full_name}. Recordar que la contraseña temporal es ${PASSWORD_CREATE_USER}`
          );
          settitleTypeEditCreate("Usuario creado exitosamente");
        }
        succeed(resp);
        toggleModal();
        showModalHide();
      },
      onError: (error, params: any) => {
        params.failed();
        const errorMessage = getErrorValueResponse(error);
        toast.current.show({
          severity: "error",
          summary: "Atención",
          detail: errorMessage,
          contentClassName: "",
          life: 5000,
        });
      },
    }
  );
  const asyncExecutorPoolProfileAgent = useAsyncExecutorPool("");
  const loadRoleProfilesAgent: any = asyncExecutorPoolProfileAgent.getExecutor(
    "loadRolProfilesUserAgent"
  );
  const asyncExecutorPoolRolProcessesAgent = useAsyncExecutorPool("");
  const loadRolProcessesAgent: any =
    asyncExecutorPoolRolProcessesAgent.getExecutor("loadRolProcessesUserAgent");
  useEffect(() => {
    if (!loadRoleProfilesAgent || !loadRolProcessesAgent) return;
  }, [
    loadRoleProfilesAgent,
    loadRolProcessesAgent,
    asyncExecutorPoolProfileAgent,
    asyncExecutorPoolRolProcessesAgent,
  ]);
  const valueLabelProcesses = loadRolProcessesAgent?.data?.processes.map(
    (process) => {
      return {
        label: `${process.name}`,
        value: `${process.id}`,
      };
    }
  );
  const valueLabelProfilesFilter = loadRoleProfilesAgent?.data?.roles
    .find((role) => {
      return role.name === userProfileMe.role.name;
    })
    .profiles.filter((profilefilter) => {
      if (showProfileTwoShowUser) {
        return profilefilter.name !== "";
      }
      return profilefilter.name !== "Administrador";
    })
    .map((profile) => {
      return {
        label: `${profile.name} de ${userProfileMe.role.name}`,
        value: `${profile.id}`,
      };
    });

  const [documentNumberMaxLength, setSocumentNumberMaxLength] = useState(
    DOCUMENT_NUMBER_MAX_LENGTH
  );
  return (
    <DivOverMaxHeigthIntra>
      <WForm
        formConfig={formConfig}
        globalErrorMessages={{
          minlength: (val) => {
            return `El valor tiene ${val.actualLength}. Valor mínimo requerido ${val.requiredLength}`;
          },
          maxlength: (val) => {
            return `Máximo Valor ${val.requiredLength}`;
          },
          required: "Campo requerido",
          emailIsTaken: "Ya existe una cuenta creada con este correo",
        }}
      >
        <Toast ref={toast}></Toast>
        <div className="p-grid" style={{ display: "flex" }}>
          <div className="p-md-2" style={{ width: "53.3rem" }}>
            <WFormControl
              name="user.full_name"
              errorMessages={{ pattern: "Solo mayúsculas" }}
            >
              <TemplateInputContainer label="Nombres y apellidos:">
                <WInputText
                  width="53.3rem"
                  height="5rem"
                  fontSize="1.4rem"
                  placeholder="NOMBRES Y APELLIDOS"
                  maxLength={FULL_NAME_MAX_LENGTH}
                  onInput={inputUperCaseFn}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl
              name="user.email"
              errorMessages={{ pattern: "Email inválido" }}
            >
              <TemplateInputContainer label="Correo electrónico:">
                <WInputText
                  width="25.6rem"
                  placeholder="Correo electrónico"
                  maxLength={EMAIL_MAX_LENGTH}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl name="user.job">
              <TemplateInputContainer label="Cargo:">
                <WInputText width="25.6rem" placeholder="Escriba el cargo" />
              </TemplateInputContainer>
            </WFormControl>
          </div>
        </div>
        <div
          className="p-grid"
          style={{ display: "flex", marginTop: "3.2rem" }}
        >
          <div className="p-md-2" style={{ width: "25.6rem" }}>
            <WFormControl name="user.document_type">
              <TemplateInputContainer label="Tipo de documento:">
                <WDropdown
                  options={[
                    { label: "DNI", value: "dni" },
                    { label: "Pasaporte", value: "passport" },
                    {
                      label: "Carnet de extranjería",
                      value: "immigration_card",
                    },
                  ]}
                  width="25.6rem"
                  placeholder="(Seleccione el tipo de doc)"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl name="user.document_number">
              <TemplateInputContainer label="DNI/CE/Pasaporte:">
                <WInputText
                  width="25.6rem"
                  placeholder="DNI/CE/Pasaporte"
                  maxLength={documentNumberMaxLength}
                  onInput={inputMaxLengthFn}
                  onKeyPress={onlyDigits}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl
              name="user.phone_number"
              errorMessages={{ pattern: "Ingrese datos correctos" }}
            >
              <TemplateInputContainer label="Celular:">
                <WInputText
                  width={"25.6rem"}
                  placeholder="Celular"
                  maxLength={PHONE_NUMBER_MAX_LENGTH}
                  onInput={inputMaxLengthFn}
                  onKeyPress={onlyDigits}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl name="user.rol">
              <TemplateInputContainer
                label="Rol:"
                loadStatus={loadRoleProfilesAgent?.status}
              >
                <WDropdown
                  options={valueRolDataMe}
                  width="25.6rem"
                  placeholder="(Seleccione los roles)"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
        </div>
        <div
          className="p-grid"
          style={{ display: "flex", marginTop: "3.2rem" }}
        >
          <div className="p-md-2" style={{ width: "25.6rem" }}>
            <WFormControl name="profile_id">
              <TemplateInputContainer
                label="Perfil:"
                loadStatus={loadRoleProfilesAgent?.status}
              >
                <WDropdown
                  options={valueLabelProfilesFilter || []}
                  width={"25.6rem"}
                  placeholder="(Seleccione un perfil)"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "53.3rem", marginLeft: "2rem" }}
          >
            <WFormControl name="processes">
              <TemplateInputContainer
                label="Proceso:"
                loadStatus={loadRolProcessesAgent?.status}
              >
                <WMultiSelect
                  options={valueLabelProcesses || []}
                  width={"53.3rem"}
                  placeholder="(Seleccione uno o más procesos)"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <Authorization actions={["user.update_users", "user.create_users"]}>
            {userInfo.email === dataUserEdit.email &&
            userInfo.profile.name === "Administrador" ? (
              <div />
            ) : (
              <SaveButton
                label={dataUserEdit ? "Guardar usuario" : "Crear nuevo usuario"}
                iconRight={IconVectorRightBlue}
                width="25.6rem"
                height="6rem"
                fontSize="1.5rem"
                backgroundColor="#88B13B"
              />
            )}
          </Authorization>
        </div>
      </WForm>
    </DivOverMaxHeigthIntra>
  );
};

export default UsersManagementStepTwoForm;
